/**
 * Schedule API — TanStack Query hooks for the auto-scheduler surface.
 * Uses the shared `api` instance (base '/api/v1', envelope-normalised errors).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { WorkItemScheduleItem, SchedulerSetting } from '@/types/schedule';

interface ApiEnvelope<T> {
  data: T;
  error: { code: string; message: string } | null;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const SCHEDULE_KEYS = {
  portfolio: ['schedule', 'portfolio'] as const,
  resource: (memberId: string) => ['schedule', 'resource', memberId] as const,
  setting: ['scheduler-setting'] as const,
};

// ---------------------------------------------------------------------------
// GET /schedule/portfolio — all work-item schedule rows
// ---------------------------------------------------------------------------

async function fetchPortfolioSchedule(): Promise<WorkItemScheduleItem[]> {
  const res = await api.get<ApiEnvelope<{ items: WorkItemScheduleItem[] }>>(
    '/schedule/portfolio',
  );
  return res.data.data.items;
}

export function usePortfolioSchedule() {
  return useQuery({
    queryKey: SCHEDULE_KEYS.portfolio,
    queryFn: fetchPortfolioSchedule,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// GET /schedule/resource/{id} — single-resource view
// ---------------------------------------------------------------------------

async function fetchResourceSchedule(memberId: string): Promise<WorkItemScheduleItem[]> {
  const res = await api.get<ApiEnvelope<{ items: WorkItemScheduleItem[] }>>(
    `/schedule/resource/${memberId}`,
  );
  return res.data.data.items;
}

export function useResourceSchedule(memberId: string) {
  return useQuery({
    queryKey: SCHEDULE_KEYS.resource(memberId),
    queryFn: () => fetchResourceSchedule(memberId),
    staleTime: 60_000,
    enabled: !!memberId,
  });
}

// ---------------------------------------------------------------------------
// GET /scheduler-setting
// ---------------------------------------------------------------------------

async function fetchSchedulerSetting(): Promise<SchedulerSetting> {
  const res = await api.get<ApiEnvelope<SchedulerSetting>>('/scheduler-setting');
  return res.data.data;
}

export function useSchedulerSetting() {
  return useQuery({
    queryKey: SCHEDULE_KEYS.setting,
    queryFn: fetchSchedulerSetting,
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// POST /schedule/recompute — trigger re-scheduling
// ---------------------------------------------------------------------------

export function useRecompute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<ApiEnvelope<void>>('/schedule/recompute').then(() => undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SCHEDULE_KEYS.portfolio });
      void qc.invalidateQueries({ queryKey: SCHEDULE_KEYS.setting });
    },
  });
}
